import { useSelector } from 'react-redux'
import { pathOr } from 'ramda'

import { useGetUserDataBeforeCancel } from 'src/api/account/getUserDataBeforCancel'
import { usePromotions } from './usePromotions'
import { getShippingDays } from 'src/utils/delivery'
import { useMembershipData } from 'src/api/Membership/useMembershipData'
import { gql, useQuery as useApolloQuery } from '@apollo/client'
import { useAllowedToSeeMembership } from 'src/api/Membership/useIsAllowedToSeeMembership'

const ID_PROMOTION_CANCELATION = 85
const ECONOMIC_PLAN_SEGMENTS = [4, 6, 8]
const PROMOTION_ECONOMIC_PLAN_IDS = [173, 176, 285]
const IDLE_DELIVERY_DAYS = [3, 4, 5, 6] // change in subs-back if this is updated

type IdleDeliveryDaysData = {
  idleDeliveryDays: {
    days: number[]
  }
}

type DietsData = {
  id: number
  name: string
  label: string
  description: string
  image: string
}

/**
 * Hook to get the variables for the brightback widget
 * TODO: this hook should be removed, and replaced by a query to sdui to get the data.
 * Extend a new query from the current /user endpoint should be a good start.
 */
export function useBrightback() {
  const {
    isAllowedToSeeMembership,
    loading: isLoadingIsAllowedToSeeMembership
  } = useAllowedToSeeMembership()
  const {
    membershipData,
    loading: isLoadingMembershipData
  } = useMembershipData()

  const {
    data,
    loading: isLoadingUserDataBeforeCancel
  } = useGetUserDataBeforeCancel()
  const {
    storePromotions,
    availablePromotions,
    isLoading: isLoadingPromotions
  } = usePromotions()

  const plan = useSelector(state =>
    pathOr({}, ['api', 'subscription', 'userPlan'], state)
  )
  // Assume that the plan is loading until we have data
  const isPlanLoading = Object.keys(plan).length === 0

  const { data: dietsData, loading: isLoadingDiets } = useApolloQuery<{
    diets: DietsData[]
  }>(gql`
    query Diets {
      diets {
        id
        name
        label
        description
        image
      }
    }
  `)

  const subscriptionInfo = useSelector(state =>
    pathOr({}, ['api', 'subscription', 'info'], state)
  )
  const {
    addresses,
    availableDeliveryDays,
    deliveryDays,
    email,
    id,
    magento_id,
    name,
    planChanges,
    ring,
    store_id
  } = subscriptionInfo
  // Assume that the subscription info is loading until we have data
  const isSubscriptionInfoLoading = Object.keys(subscriptionInfo).length === 0

  const {
    data: idleDeliveryDaysData,
    loading: isLoadingIdleDeliveryDays
  } = useApolloQuery<IdleDeliveryDaysData, {}>(
    gql`
      query IdleDeliveryDay {
        idleDeliveryDays {
          days
        }
      }
    `
  )
  const idleDeliveryDays = idleDeliveryDaysData?.idleDeliveryDays?.days

  const isLoading = [
    isLoadingUserDataBeforeCancel,
    isLoadingIdleDeliveryDays,
    isLoadingIsAllowedToSeeMembership,
    isLoadingDiets,
    isLoadingMembershipData,
    isSubscriptionInfoLoading,
    isLoadingPromotions,
    isPlanLoading
  ].some(Boolean)

  const getAddressData = () => {
    const address = addresses?.[0]
    return address
      ? {
          zipCode: address.postcode,
          region: address.region,
          regionId: address.regionId
        }
      : {}
  }

  const isOfferCancelation = () => {
    return storePromotions?.some(
      elem => elem.promotionId === ID_PROMOTION_CANCELATION
    )
  }

  const hadEconomicPlan = () => {
    const hasEconomicPlanPromotion = storePromotions?.some(promotion =>
      PROMOTION_ECONOMIC_PLAN_IDS.includes(promotion.promotionId)
    )
    const hasEconomicPlan = planChanges?.some(planChange =>
      ECONOMIC_PLAN_SEGMENTS.includes(planChange?.plan?.segmentId)
    )
    return hasEconomicPlanPromotion || hasEconomicPlan
  }

  const hasAvailablePromotions = () => {
    return availablePromotions && availablePromotions.length > 0
  }

  const acceptsDeliveryDay = (indexAvailableDeliveryDays: number) => {
    if (availableDeliveryDays?.length === 0) {
      return false
    }

    const isAvailable = availableDeliveryDays?.some(
      availableDeliveryDay =>
        availableDeliveryDay.day === indexAvailableDeliveryDays
    )
    return isAvailable
  }

  function canChangeToIdleDeliveryDay(): boolean {
    // convert deliveryDays to shippingDays
    const shippingDays = getShippingDays(
      ring?.shipping_days ?? 0,
      ...(idleDeliveryDays ?? [])
    )

    // verify that at least one shipping day is idle
    return shippingDays.some(shippingDay =>
      IDLE_DELIVERY_DAYS.some(idleDay => shippingDay === idleDay)
    )
  }

  function isUnityPassActive(): boolean {
    return (
      membershipData?.activation.active === true &&
      membershipData?.activation.stopDate === null
    )
  }

  const brightBackVariables = {
    app_id: process.env.REACT_APP_BRIGHTBACK_ID,
    email: email,
    first_name: name,
    last_name: name,
    subscription_id: id,
    save_return_url: process.env.REACT_APP_BRIGHTBACK_SAVE_URL,
    cancel_confirmation_url: process.env.REACT_APP_BRIGHTBACK_CANCEL_URL,
    account: {
      internal_id: magento_id,
      subscription_id: id
    },
    custom: {
      subscription_plan: plan.mealsPerDelivery,
      delivery_day: deliveryDays,
      completedOrders: data?.countOrdersCompleted,
      lifespan_segment: data?.weeksSinceFirstDeliveryWhileActive,
      hasEconomicPlan: plan.type === 'ECONOMIC',
      ...getAddressData(),
      unity_pass_active: isUnityPassActive(),
      storeId: store_id,
      offer_eligible: !isOfferCancelation(),
      diets: dietsData?.diets,
      hadEconomicPlan: hadEconomicPlan(),
      hasAvailablePromotions: hasAvailablePromotions(),
      acceptsTuesday: acceptsDeliveryDay(2),
      acceptsWednesday: acceptsDeliveryDay(3),
      acceptsThursday: acceptsDeliveryDay(4),
      idle_available: canChangeToIdleDeliveryDay(),
      allow_pause: plan.segment?.allowPause,
      allow_skip: plan.segment?.allowSkip,
      unity_pass_available: !!isAllowedToSeeMembership?.isAllowed,
      delivery_type: ring?.is_local
        ? `Local ${ring?.shipping_days}`
        : `Regional ${ring?.shipping_days}`
    }
  }

  return { variables: isLoading ? null : brightBackVariables, isLoading }
}
